import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, Clock, Cpu, MapPin, Gauge, Compass, Mountain, Satellite, Power, Activity } from 'lucide-react';
import { formatDT, formatSpeed, formatDateOnly as fmtDate, formatTimeOnly as fmtTime, resolveTimezone } from '../../utils/timezone';
import { getTimezoneLabel } from '../../utils/timezoneList';
import { formatRecordDeviceLabel } from '../../utils/deviceLabel';

/**
 * Renders an Event Marker icon on the map (used by both Google Maps AdvancedMarker and Leaflet).
 * When clicked, fetches forensic IO telemetry data and displays it in a popup modal.
 */

// ── Severity color map for marker icon + pulse ring ──
const SEVERITY_COLORS = {
  critical: { shield: '#dc2626', ring: '#fca5a5' },
  warning:  { shield: '#f59e0b', ring: '#fde68a' },
  info:     { shield: '#2563eb', ring: '#93c5fd' },
};

// ── Marker Visual (shared by both map providers) ──
export const EventMarkerIcon = ({ severity = 'warning', pulse = true }) => {
  const colors = SEVERITY_COLORS[severity] || SEVERITY_COLORS.warning;
  return (
    <div
      style={{ width: 0, height: 0, overflow: 'visible', cursor: 'pointer' }}
      data-testid="event-map-marker"
    >
      {/* Pulse ring — severity-colored */}
      {pulse && (
        <div style={{
          position: 'absolute',
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: colors.ring,
          opacity: 0.5,
          transform: 'translate(-50%, -60%)',
          animation: 'eventMarkerPulse 2s ease-out infinite',
        }} />
      )}
      {/* Event Icon — shield with "E", severity-colored */}
      <div style={{
        position: 'absolute',
        transform: 'translate(-50%, -100%)',
        filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))',
        zIndex: 1,
      }}>
        <svg width="36" height="36" viewBox="0 0 514 512" xmlns="http://www.w3.org/2000/svg">
          <path fill={colors.shield} d="M420.133,0H94.315C68.715,0,47.77,20.945,47.77,46.545v300.219c0,16.291,6.982,30.254,20.945,39.563L257.225,512l188.51-125.673c11.637-9.31,20.943-23.272,20.943-39.563V46.545C466.678,20.945,445.732,0,420.133,0z"/>
          <path fill="#FFFFFF" d="M189.555,314.22V116.897h146.309v33.381H229.396v43.744h99.064v33.246h-99.064v53.705h110.236v33.246H189.555z"/>
        </svg>
      </div>
      {/* Inject pulse keyframes */}
      <style>{`
        @keyframes eventMarkerPulse {
          0% { transform: translate(-50%, -60%) scale(0.8); opacity: 0.5; }
          70% { transform: translate(-50%, -60%) scale(2); opacity: 0; }
          100% { transform: translate(-50%, -60%) scale(2); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

// ── Color name → hex mapping (same as History Forensics) ──
const COLOR_NAME_TO_HEX = {
  'red': '#EF4444', 'green': '#22C55E', 'blue': '#3B82F6', 'orange': '#F97316',
  'purple': '#A855F7', 'teal': '#14B8A6', 'amber': '#F59E0B', 'rose': '#F43F5E', 'yellow': '#FACC15',
};

const getMappedValueColor = (ioId, displayValue, rawValue, ioMappings) => {
  if (!ioMappings || displayValue == null) return null;
  const mapping = ioMappings.find(m => m.io_id === ioId);
  if (!mapping?.value_colors) return null;
  const vc = mapping.value_colors;
  if (typeof vc !== 'object') return null;
  const colorName = vc[String(displayValue)]
    || (rawValue != null ? vc[String(rawValue)] : null)
    || vc[String(displayValue).toUpperCase()]
    || vc[String(displayValue).toLowerCase()]
    || vc['_default']
    || null;
  if (!colorName) return null;
  return COLOR_NAME_TO_HEX[colorName] || colorName;
};

// ── Format date/time helpers (delegate to the shared timezone util so they respect
// the user's configured date/time format — never hardcode the locale here) ──
const formatDateOnly = (isoStr, opts) => (isoStr ? fmtDate(isoStr, opts) : '—');
const formatTimeOnly = (isoStr, opts) => (isoStr ? fmtTime(isoStr, { ...opts, seconds: true }) : '');

// ── Forensic Telemetry Popup Modal ──
export const ForensicTelemetryModal = ({ open, onOpenChange, occurrenceId, occurrence }) => {
  const { api } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [occData, setOccData] = useState(occurrence || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !occurrenceId) return;
    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        const [occRes, snapRes] = await Promise.all([
          !occData ? api.get(`/event-occurrences/${occurrenceId}`) : Promise.resolve({ data: occData }),
          api.get(`/event-occurrences/${occurrenceId}/snapshot`),
        ]);
        if (occRes.data) setOccData(occRes.data);
        setSnapshot(snapRes.data);
      } catch {
        setError('Failed to load telemetry data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [open, occurrenceId, api]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSpeed = occData?.event_type?.includes('overspeed');
  const eventTz = occData?.device_timezone || resolveTimezone();
  const tzLabel = getTimezoneLabel(eventTz) || eventTz;

  const severityColor = occData?.severity === 'critical'
    ? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
    : occData?.severity === 'warning'
    ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
    : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="forensic-telemetry-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Cpu className="w-5 h-5 text-blue-500" />
            Forensic Telemetry Snapshot
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8" data-testid="forensic-loading">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading telemetry data...</span>
          </div>
        )}

        {error && (
          <div className="p-4 text-center text-sm text-red-500" data-testid="forensic-error">
            {error}
          </div>
        )}

        {!loading && !error && occData && (
          <div className="space-y-3">
            {/* Event Summary — includes event date + GPS from occurrence */}
            <div className="p-3 rounded-lg bg-muted/50 border space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{occData.event_rule_name}</span>
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${severityColor}`}>
                  {occData.severity}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground" data-testid="event-map-marker-device-label">{formatRecordDeviceLabel(occData)}</div>
              {isSpeed && occData.speed_kmh != null && (
                <div className="text-xs font-medium text-red-600 dark:text-red-400">
                  {formatSpeed(occData.speed_kmh)}{occData.threshold_kmh ? ` (Limit: ${formatSpeed(occData.threshold_kmh)})` : ''}
                </div>
              )}
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                {formatDT(occData.triggered_at, { timezone: eventTz, seconds: true })} — {tzLabel}
              </div>
              {occData.latitude && occData.longitude && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="w-3 h-3" />
                  {Number(occData.latitude).toFixed(5)}, {Number(occData.longitude).toFixed(5)}
                </div>
              )}
            </div>

            {/* Telemetry Data Grid — matches History Forensic layout */}
            {snapshot ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="forensic-io-data">
                {/* Time (Position) */}
                <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1 mb-1">
                    <Clock className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                    <span className="text-[10px] text-muted-foreground">Time (Position)</span>
                  </div>
                  <p className="text-xs font-semibold">{formatDateOnly(snapshot.position_timestamp, { timezone: eventTz })}</p>
                  <p className="text-[11px] text-muted-foreground">{formatTimeOnly(snapshot.position_timestamp, { timezone: eventTz })}</p>
                </div>
                {/* Time (Server) */}
                <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1 mb-1">
                    <Clock className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                    <span className="text-[10px] text-muted-foreground">Time (Server)</span>
                  </div>
                  <p className="text-xs font-semibold">{formatDateOnly(snapshot.server_timestamp, { timezone: eventTz })}</p>
                  <p className="text-[11px] text-muted-foreground">{formatTimeOnly(snapshot.server_timestamp, { timezone: eventTz })}</p>
                </div>
                {/* Position */}
                <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1 mb-1">
                    <MapPin className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Position</span>
                  </div>
                  <p className="text-xs font-semibold">
                    {snapshot.lat != null && snapshot.lng != null ? (
                      <a href={`https://www.google.com/maps?q=${snapshot.lat},${snapshot.lng}`} target="_blank" rel="noopener noreferrer" className="text-[#2563eb] hover:underline">
                        {Number(snapshot.lat).toFixed(6)}, {Number(snapshot.lng).toFixed(6)}
                      </a>
                    ) : '—'}
                  </p>
                </div>
                {/* Speed */}
                <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1 mb-1">
                    <Gauge className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Speed</span>
                  </div>
                  <p className="text-xs font-semibold">{snapshot.speed != null ? formatSpeed(snapshot.speed) : '—'}</p>
                </div>
                {/* Heading */}
                <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1 mb-1">
                    <Compass className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Heading</span>
                  </div>
                  <p className="text-xs font-semibold">{snapshot.heading ?? '—'}&deg;</p>
                </div>
                {/* Altitude */}
                <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1 mb-1">
                    <Mountain className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Altitude</span>
                  </div>
                  <p className="text-xs font-semibold">{snapshot.altitude ?? '—'} m</p>
                </div>
                {/* Satellites */}
                <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1 mb-1">
                    <Satellite className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Satellites</span>
                  </div>
                  <p className="text-xs font-semibold">{snapshot.satellites ?? '—'}</p>
                </div>
                {/* Ignition */}
                <div className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1 mb-1">
                    <Power className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Ignition</span>
                  </div>
                  <p className={`text-xs font-semibold ${snapshot.ignition ? 'text-green-600' : 'text-red-500'}`}>
                    {snapshot.ignition ? 'ON' : 'OFF'}
                  </p>
                </div>
                {/* IO Mapped Values */}
                {Object.entries(snapshot.mapped_values || {}).map(([key, mv]) => {
                  const valColor = getMappedValueColor(mv.io_id, mv.type === 'state' ? mv.value : null, mv.raw, snapshot.io_mappings);
                  return (
                    <div key={key} className="rounded-lg border p-2.5 bg-muted/20 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-1 mb-1">
                        <Activity className="w-3 h-3 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground">{key}</span>
                      </div>
                      <p className="text-xs font-semibold break-all">
                        {mv.type === 'state' ? (
                          <span style={valColor ? { color: valColor } : undefined} className={!valColor ? (mv.value === 'ON' || mv.value === 'Open' ? 'text-green-600' : mv.value === 'OFF' || mv.value === 'Closed' ? 'text-red-500' : '') : ''}>
                            {mv.value}
                          </span>
                        ) : (
                          <span className="break-all">{mv.value} {mv.units}</span>
                        )}
                      </p>
                      <p className="text-[9px] text-muted-foreground break-all">Raw: {mv.raw}</p>
                    </div>
                  );
                })}
              </div>
            ) : !loading && (
              <div className="text-center py-4 text-sm text-muted-foreground" data-testid="forensic-no-data">
                No telemetry data available for this event
              </div>
            )}

            {/* IMEI footer */}
            {snapshot?.imei && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground pt-1 border-t">
                <Cpu className="w-3 h-3" /> IMEI: {snapshot.imei}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForensicTelemetryModal;
